import * as React from 'react'

import { AdminIcon, CheckboxField } from '@/modules/shared/components'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

export type PickerItem = { id: number | string; label: string }

type Props = {
  title: string
  items: PickerItem[]
  checkedIds: Array<number | string>
  onToggle: (id: number | string) => void
  /** Podświetlenie wiersza - używane przez listę grup. */
  activeId?: number | string | null
  onRowClick?: (id: number | string) => void
  idPrefix: string
}

/**
 * Lista z wyszukiwarką i checkboxami - trzy kolumny zakładki Grupy firm są tym samym
 * widżetem, więc stoją na jednym komponencie zamiast trzech definicji react-table.
 */
export const PickerList: React.FC<Props> = ({
  title,
  items,
  checkedIds,
  onToggle,
  activeId,
  onRowClick,
  idPrefix,
}) => {
  const { t } = useLocalize()
  const [phrase, setPhrase] = React.useState('')

  const filtered = React.useMemo(() => {
    const needle = phrase.trim().toLowerCase()
    if (!needle) return items
    return items.filter((item) => item.label.toLowerCase().includes(needle))
  }, [items, phrase])

  return (
    <div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-border tw-border-base-300">
      <div className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-base-300 tw-px-3 tw-py-2">
        <AdminIcon name="search" />
        <input
          className="tw-w-full tw-border-0 tw-bg-transparent tw-text-sm tw-outline-none"
          placeholder={t('administrationPanel-companies.find')}
          value={phrase}
          onChange={(event) => setPhrase(event.target.value)}
        />
      </div>
      <div className="tw-border-b tw-border-base-300 tw-px-3 tw-py-2 tw-text-xs tw-uppercase tw-text-content-base tw-opacity-80">
        {title} ({items.length})
      </div>
      <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
        {filtered.map((item) => (
          <div
            key={item.id}
            onClick={() => onRowClick?.(item.id)}
            className={`tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-base-300 tw-px-3 tw-py-2 tw-text-sm ${
              onRowClick ? 'tw-cursor-pointer' : ''
            } ${activeId === item.id ? 'tw-bg-primary/10 tw-font-medium' : 'hover:tw-bg-base-200'}`}
          >
            <CheckboxField
              id={`${idPrefix}-${item.id}`}
              checked={checkedIds.includes(item.id)}
              onChange={() => onToggle(item.id)}
            />
            <span className="tw-truncate" title={item.label}>
              {item.label}
            </span>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="tw-text-content-muted tw-px-3 tw-py-6 tw-text-center tw-text-sm">
            {t('common.no-data')}
          </div>
        )}
      </div>
    </div>
  )
}
